import { generateKeyBetween } from "fractional-indexing";
import type { Item, Label, Project, Section } from "../api/types";
import type { SyncState } from "../sync/state";
import { dueLocal } from "./dates";

type Ordered = { order_key?: string | null; child_order?: number; section_order?: number; id: string };

const LOCAL_KEY = "￿";

/** Sibling order: `order_key` when both have one, else `child_order` / `section_order`. */
export function compareOrder(a: Ordered, b: Ordered): number {
  const ak = a.order_key;
  const bk = b.order_key;
  if (ak && bk && ak !== bk) return ak < bk ? -1 : 1;
  const ao = a.child_order ?? a.section_order ?? 0;
  const bo = b.child_order ?? b.section_order ?? 0;
  if (ao !== bo) return ao - bo;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

const realKey = (k: string | null | undefined) => (k && k !== LOCAL_KEY ? k : null);

/** A fractional key between two neighbors (either may be missing). */
export function keyBetween(prev: Ordered | undefined, next: Ordered | undefined): string {
  const a = realKey(prev?.order_key);
  const b = realKey(next?.order_key);
  try {
    return generateKeyBetween(a, b && a && b <= a ? null : b);
  } catch {
    return generateKeyBetween(a, null);
  }
}

export const isLive = (state: SyncState, item: Item) => !item.checked && Boolean(state.projects[item.project_id]);

export function childrenOf(state: SyncState, parentId: string, includeCompleted = false): Item[] {
  return Object.values(state.items)
    .filter((i) => i.parent_id === parentId && (includeCompleted || !i.checked))
    .sort(compareOrder);
}

export function subtaskCounts(state: SyncState, parentId: string): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const i of Object.values(state.items)) {
    if (i.parent_id !== parentId) continue;
    total++;
    if (i.checked) done++;
  }
  return { done, total };
}

export function sectionsOf(state: SyncState, projectId: string): Section[] {
  return Object.values(state.sections)
    .filter((s) => s.project_id === projectId)
    .sort(compareOrder);
}

/** Root (parentless, or parent not in the same list) tasks of a project/section. */
export function rootTasks(
  state: SyncState,
  projectId: string,
  sectionId: string | null,
  includeCompleted = false,
): Item[] {
  return Object.values(state.items)
    .filter(
      (i) =>
        i.project_id === projectId &&
        (i.section_id ?? null) === sectionId &&
        !i.parent_id &&
        (includeCompleted || !i.checked),
    )
    .sort(compareOrder);
}

export interface ProjectNode {
  project: Project;
  children: ProjectNode[];
  depth: number;
}

export function projectTree(projects: Project[]): ProjectNode[] {
  const ids = new Set(projects.map((p) => p.id));
  const byParent = new Map<string | null, Project[]>();
  for (const p of projects) {
    const parent = p.parent_id && ids.has(p.parent_id) ? p.parent_id : null;
    const list = byParent.get(parent) ?? [];
    list.push(p);
    byParent.set(parent, list);
  }
  const build = (parent: string | null, depth: number): ProjectNode[] =>
    (byParent.get(parent) ?? [])
      .sort(compareOrder)
      .map((project) => ({ project, depth, children: build(project.id, depth + 1) }));
  return build(null, 0);
}

export function flattenTree(nodes: ProjectNode[], skipCollapsed = false): ProjectNode[] {
  const out: ProjectNode[] = [];
  const walk = (list: ProjectNode[]) => {
    for (const n of list) {
      out.push(n);
      if (!(skipCollapsed && n.project.is_collapsed)) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Projects shown under "My Projects" (personal, not the Inbox). */
export function personalProjects(state: SyncState): Project[] {
  return Object.values(state.projects).filter((p) => !p.inbox_project && !p.workspace_id);
}

export function activeCount(state: SyncState, projectId: string): number {
  let n = 0;
  for (const i of Object.values(state.items)) if (i.project_id === projectId && !i.checked) n++;
  return n;
}

export function labelCount(state: SyncState, name: string): number {
  let n = 0;
  for (const i of Object.values(state.items))
    if (!i.checked && i.labels.includes(name) && state.projects[i.project_id]) n++;
  return n;
}

export function sortedLabels(state: SyncState): Label[] {
  return Object.values(state.labels).sort((a, b) => a.item_order - b.item_order || a.name.localeCompare(b.name));
}

/** Tasks due on or before `day` (overdue + that day), excluding completed and sub-tasks whose parent also shows. */
export function tasksDueBy(state: SyncState, tz: string): (day: string) => Item[] {
  return (day) => Object.values(state.items).filter((i) => isLive(state, i) && i.due && dueLocal(i.due, tz).day <= day);
}

export function dueDay(item: Item, tz: string): string | null {
  return item.due ? dueLocal(item.due, tz).day : null;
}

export type SortBy = "manual" | "due_date" | "added_date" | "priority" | "alphabetically" | "deadline";
export type GroupBy = "none" | "due_date" | "added_date" | "priority" | "label" | "project";

export function sortTasks(
  list: Item[],
  by: string | null | undefined,
  order: string | null | undefined,
  tz: string,
): Item[] {
  const dir = order === "desc" ? -1 : 1;
  const sorted = [...list];
  const cmp = (a: Item, b: Item): number => {
    switch (by) {
      case "due_date": {
        const ad = a.due ? dueLocal(a.due, tz) : null;
        const bd = b.due ? dueLocal(b.due, tz) : null;
        if (!ad && !bd) return 0;
        if (!ad) return 1;
        if (!bd) return -1;
        const ak = `${ad.day}${ad.time ?? "99"}`;
        const bk = `${bd.day}${bd.time ?? "99"}`;
        return ak < bk ? -dir : ak > bk ? dir : 0;
      }
      case "deadline": {
        const a1 = a.deadline?.date ?? "9999";
        const b1 = b.deadline?.date ?? "9999";
        return a1 < b1 ? -dir : a1 > b1 ? dir : 0;
      }
      case "added_date":
        return a.added_at < b.added_at ? -dir : a.added_at > b.added_at ? dir : 0;
      case "priority":
        return (b.priority - a.priority) * dir;
      case "alphabetically":
        return a.content.localeCompare(b.content) * dir;
      default:
        return 0;
    }
  };
  sorted.sort((a, b) => cmp(a, b) || compareOrder(a, b));
  return sorted;
}

/** Today / Upcoming sort: by time of day (timed first), then priority, then day_order/child order. */
export function agendaSort(list: Item[], tz: string): Item[] {
  return [...list].sort((a, b) => {
    const at = a.due ? (dueLocal(a.due, tz).time ?? "99:99") : "99:99";
    const bt = b.due ? (dueLocal(b.due, tz).time ?? "99:99") : "99:99";
    if (at !== bt) return at < bt ? -1 : 1;
    if (a.day_order !== b.day_order && a.day_order >= 0 && b.day_order >= 0) return a.day_order - b.day_order;
    return b.priority - a.priority || compareOrder(a, b);
  });
}

/** Case- and width-insensitive substring search over tasks and projects. */
export function fold(s: string): string {
  return s
    .replace(/[！-～]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .toLowerCase()
    .normalize("NFKC");
}
